import { supabase } from '../lib/supabase';

const TABLE_NAME = 'task_notifications';

// Get all unread notifications for the current user (admins see all, users see their own)
export const getUnreadNotifications = async () => {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData?.user) return [];

  // Check if user is admin or manager
  const { data: user } = await supabase
    .from('users')
    .select('role')
    .eq('id', userData.user.id)
    .single();

  const isAdminOrManager = user?.role === 'admin' || user?.role === 'manager';

  let query = supabase
    .from(TABLE_NAME)
    .select('*')
    .eq('is_read', false)
    .order('created_at', { ascending: false });

  if (!isAdminOrManager) {
    query = query.or(`assigned_to.eq.${userData.user.id},completed_by.eq.${userData.user.id}`);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data || [];
};

// Get all notifications (read and unread)
export const getAllNotifications = async () => {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData?.user) return [];

  // Check if user is admin or manager
  const { data: user } = await supabase
    .from('users')
    .select('role')
    .eq('id', userData.user.id)
    .single();

  const isAdminOrManager = user?.role === 'admin' || user?.role === 'manager';

  let query = supabase
    .from(TABLE_NAME)
    .select('*')
    .order('created_at', { ascending: false })
    .limit(50);

  // If not admin/manager, show notifications for tasks they were assigned or completed
  if (!isAdminOrManager) {
    query = query.or(`assigned_to.eq.${userData.user.id},completed_by.eq.${userData.user.id}`);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data || [];
};

// Create a task completion notification
export const createTaskNotification = async (notificationData) => {
  const { data, error } = await supabase
    .from(TABLE_NAME)
    .insert([notificationData])
    .select()
    .single();

  if (error) throw error;
  return data;
};

// Mark notification as read
export const markNotificationAsRead = async (notificationId) => {
  const { data, error } = await supabase
    .from(TABLE_NAME)
    .update({ is_read: true })
    .eq('id', notificationId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

// Mark all notifications as read
export const markAllNotificationsAsRead = async () => {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData?.user) return;

  // Check if user is admin or manager
  const { data: user } = await supabase
    .from('users')
    .select('role')
    .eq('id', userData.user.id)
    .single();

  const isAdminOrManager = user?.role === 'admin' || user?.role === 'manager';

  let query = supabase
    .from(TABLE_NAME)
    .update({ is_read: true })
    .eq('is_read', false);

  // If not admin/manager, only mark their own notifications
  if (!isAdminOrManager) {
    query = query.or(`assigned_to.eq.${userData.user.id},completed_by.eq.${userData.user.id}`);
  }

  const { error } = await query;
  if (error) throw error;
};

// Get unread notification count
export const getUnreadNotificationCount = async () => {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData?.user) return 0;

  // Check if user is admin or manager
  const { data: user } = await supabase
    .from('users')
    .select('role')
    .eq('id', userData.user.id)
    .single();

  const isAdminOrManager = user?.role === 'admin' || user?.role === 'manager';

  let query = supabase
    .from(TABLE_NAME)
    .select('id', { count: 'exact', head: true })
    .eq('is_read', false);

  // If not admin/manager, only count their own notifications
  if (!isAdminOrManager) {
    query = query.or(`assigned_to.eq.${userData.user.id},completed_by.eq.${userData.user.id}`);
  }

  const { count, error } = await query;

  if (error) throw error;
  return count || 0;
};

// Delete a notification
export const deleteNotification = async (notificationId) => {
  const { error } = await supabase
    .from(TABLE_NAME)
    .delete()
    .eq('id', notificationId);

  if (error) throw error;
};

// Get the latest notification for a specific client
export const getLatestNotificationForClient = async (clientId) => {
  const { data, error } = await supabase
    .from(TABLE_NAME)
    .select('*')
    .eq('client_id', clientId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return data;
};
